import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Link2,
  Loader2,
  Plus,
  Save,
  Trash2,
} from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import type { Doc, Id } from '../../../convex/_generated/dataModel'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Loading } from '@/components/ui/loading'
import { OrganizationPriceListAdoption } from './OrganizationPriceListAdoption'

const emptyFields = {
  nome: '',
  partita_iva: '',
  codice_fiscale: '',
  indirizzo: '',
  citta: '',
  cap: '',
  provincia: '',
  telefono: '',
  email: '',
  pec: '',
  sdi: '',
}
type OrganizationFields = typeof emptyFields
const fields: {
  key: keyof OrganizationFields
  label: string
  type?: string
  maxLength?: number
}[] = [
  { key: 'partita_iva', label: 'Partita IVA', maxLength: 11 },
  { key: 'codice_fiscale', label: 'Codice fiscale', maxLength: 16 },
  { key: 'indirizzo', label: 'Indirizzo' },
  { key: 'citta', label: 'Città' },
  { key: 'cap', label: 'CAP', maxLength: 5 },
  { key: 'provincia', label: 'Provincia', maxLength: 2 },
  { key: 'telefono', label: 'Telefono', type: 'tel' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'pec', label: 'PEC', type: 'email' },
  { key: 'sdi', label: 'Codice SDI', maxLength: 7 },
]
const selectClass =
  'flex min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'

function messageFrom(error: unknown, fallback: string) {
  return error instanceof Error && error.message.length < 300
    ? error.message
    : fallback
}

export default function OrganizationForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = Boolean(id)
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission('manage_companies')
  const canView = canManage || hasPermission('view_companies')
  const isLabSide =
    profile?.ruolo === 'amministratore' || profile?.ruolo === 'tecnico_lab'
  const canManageOrganizationPriceList =
    isLabSide && canManage && hasPermission('manage_listino')
  const organizationId = id as Id<'organizations'> | undefined
  const organization = useQuery(
    api.organizations.getOrganization,
    canView && organizationId ? { organizationId } : 'skip'
  )
  const organizations = useQuery(
    api.organizations.listOrganizations,
    canManage && isEdit ? {} : 'skip'
  )
  const companiesResult = useQuery(
    api.companies.listCompanies,
    canManage && isEdit ? {} : 'skip'
  )
  const priceListOverview = useQuery(
    api.organizations.getOrganizationPriceListOverview,
    canManageOrganizationPriceList && organizationId
      ? { organizationId }
      : 'skip'
  )
  const createOrganization = useMutation(api.organizations.createOrganization)
  const updateOrganization = useMutation(api.organizations.updateOrganization)
  const assignClinic = useMutation(api.organizations.assignClinic)
  const deleteOrganization = useMutation(api.organizations.deleteOrganization)
  const [form, setForm] = useState<OrganizationFields>(emptyFields)
  const [sharePriceLists, setSharePriceLists] = useState(false)
  const [saving, setSaving] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [adoptingPriceList, setAdoptingPriceList] = useState(false)
  const [selectedClinic, setSelectedClinic] = useState('')
  const [error, setError] = useState('')
  const [linkError, setLinkError] = useState('')
  const [status, setStatus] = useState('')
  const [linkStatus, setLinkStatus] = useState('')
  const loadedId = useRef<string | undefined>()
  const errorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isEdit) {
      setForm(emptyFields)
      setSharePriceLists(false)
      loadedId.current = undefined
    } else if (organization && loadedId.current !== organization._id) {
      setForm(
        Object.fromEntries(
          Object.keys(emptyFields).map((key) => [
            key,
            organization[key as keyof OrganizationFields] || '',
          ])
        ) as OrganizationFields
      )
      setSharePriceLists(organization.share_price_lists)
      loadedId.current = organization._id
    }
  }, [organization, isEdit])

  useEffect(() => {
    if (error) errorRef.current?.focus()
  }, [error])

  if (!canView || (!isEdit && !canManage))
    return (
      <p className="py-8 text-center text-gray-600">
        Non hai i permessi per accedere a questa società.
      </p>
    )
  if (isEdit && organization === undefined) return <Loading />
  if (isEdit && !organization)
    return (
      <div className="space-y-4">
        <p>Società non disponibile.</p>
        <Button asChild variant="outline">
          <Link to="/organizations">Torna alle società</Link>
        </Button>
      </div>
    )

  const companies = Array.isArray(companiesResult)
    ? (companiesResult as Doc<'companies'>[])
    : []
  const availableClinics = companies.filter(
    (company) => company.organization_id !== organizationId
  )
  const clinicToLink = availableClinics.find(
    (clinic) => clinic._id === selectedClinic
  )
  const previousOrganization = organizations?.find(
    (item) => item._id === clinicToLink?.organization_id
  )
  const busy = saving || assigning || deleting || adoptingPriceList
  const canEnableSharing =
    isEdit &&
    canManageOrganizationPriceList &&
    priceListOverview !== undefined &&
    priceListOverview.organizationServiceCount > 0

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canManage || busy) return
    if (!form.nome.trim()) {
      setError('Inserisci il nome della società.')
      return
    }
    if (
      sharePriceLists &&
      !organization?.share_price_lists &&
      !canEnableSharing
    ) {
      setError(
        'Prepara almeno una prestazione nel listino della società prima di attivare la condivisione.'
      )
      return
    }
    setSaving(true)
    setError('')
    setStatus('')
    const data = {
      ...(Object.fromEntries(
        Object.entries(form).map(([key, value]) => [key, value.trim()])
      ) as OrganizationFields),
      share_price_lists: isEdit ? sharePriceLists : false,
    }
    try {
      if (organizationId) {
        await updateOrganization({ organizationId, ...data })
        setStatus('Dati e impostazioni della società salvati.')
      } else {
        const createdId = await createOrganization(data)
        navigate(`/organizations/${createdId}/edit`, { replace: true })
        setStatus(
          'Società creata. Collega le cliniche e prepara il listino della società prima di attivare la condivisione.'
        )
      }
    } catch (caught) {
      setError(
        messageFrom(
          caught,
          'Non è stato possibile salvare la società. Controlla i dati e riprova.'
        )
      )
    } finally {
      setSaving(false)
    }
  }

  const linkClinic = async () => {
    if (!organizationId || !clinicToLink || !canManage || busy) return
    setAssigning(true)
    setLinkError('')
    setLinkStatus('')
    try {
      await assignClinic({ organizationId, companyId: clinicToLink._id })
      setLinkStatus(`Clinica “${clinicToLink.nome}” collegata alla società.`)
      setSelectedClinic('')
    } catch (caught) {
      setLinkError(
        messageFrom(
          caught,
          'Non è stato possibile collegare la clinica. Riprova.'
        )
      )
    } finally {
      setAssigning(false)
    }
  }

  const removeOrganization = async () => {
    if (
      !organizationId ||
      !canManage ||
      busy ||
      !window.confirm(
        `Eliminare la società “${organization?.nome}”? È possibile solo se non ha cliniche o listini collegati.`
      )
    )
      return
    setDeleting(true)
    setError('')
    try {
      await deleteOrganization({ organizationId })
      navigate('/organizations')
    } catch (caught) {
      setError(
        messageFrom(
          caught,
          'La società non può essere eliminata. Verifica che non abbia cliniche o listini collegati.'
        )
      )
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-start gap-3 border-b border-gray-200 pb-5">
        <Button asChild variant="outline" size="icon" className="shrink-0">
          <Link to="/organizations" aria-label="Torna alle società">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
        <div className="min-w-0">
          <h1 className="break-words text-2xl font-bold tracking-[-0.02em] text-gray-950">
            {isEdit ? organization?.nome : 'Nuova società'}
          </h1>
          <p className="mt-1 text-sm leading-6 text-gray-600">
            Anagrafica della società e impostazioni per le sue cliniche.
          </p>
        </div>
      </div>

      {error && (
        <div
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-red-700"
        >
          <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      {status && (
        <p role="status" className="text-sm text-green-800">
          {status}
        </p>
      )}

      <Card className="shadow-none">
        <CardContent className="p-5 sm:p-6">
          <form onSubmit={(event) => void save(event)} className="space-y-6">
            <fieldset disabled={!canManage || busy} className="space-y-5">
              <legend className="mb-1 text-lg font-semibold text-gray-950">
                Dati della società
              </legend>
              <p className="text-sm leading-6 text-gray-600">
                Il nome è obbligatorio. Gli altri dati sono facoltativi e
                restano distinti dalle anagrafiche delle cliniche.
              </p>
              <div>
                <label
                  htmlFor="organization-name"
                  className="mb-1.5 block text-sm font-medium text-gray-900"
                >
                  Nome società *
                </label>
                <Input
                  id="organization-name"
                  required
                  maxLength={200}
                  value={form.nome}
                  onChange={(event) => {
                    setForm({ ...form, nome: event.target.value })
                    setStatus('')
                  }}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {fields.map((field) => (
                  <div key={field.key}>
                    <label
                      htmlFor={`organization-${field.key}`}
                      className="mb-1.5 block text-sm font-medium text-gray-900"
                    >
                      {field.label}
                    </label>
                    <Input
                      id={`organization-${field.key}`}
                      type={field.type ?? 'text'}
                      maxLength={field.maxLength}
                      value={form[field.key]}
                      onChange={(event) => {
                        setForm({ ...form, [field.key]: event.target.value })
                        setStatus('')
                      }}
                    />
                  </div>
                ))}
              </div>
            </fieldset>

            <fieldset
              disabled={!canManage || busy}
              className="border-t border-gray-200 pt-6"
            >
              <legend className="float-left mb-3 w-full text-lg font-semibold text-gray-950">
                Listino della società
              </legend>
              <div className="clear-both flex items-start gap-3">
                <input
                  id="organization-share-price-lists"
                  type="checkbox"
                  checked={sharePriceLists}
                  disabled={!sharePriceLists && !canEnableSharing}
                  onChange={(event) => {
                    setSharePriceLists(event.target.checked)
                    setStatus('')
                  }}
                  aria-describedby="organization-share-hint organization-share-availability"
                  className="mt-1 h-4 w-4 shrink-0 accent-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed"
                />
                <div>
                  <label
                    htmlFor="organization-share-price-lists"
                    className="text-sm font-medium text-gray-950"
                  >
                    Usa il listino della società per tutte le cliniche
                  </label>
                  <p
                    id="organization-share-hint"
                    className="mt-1 max-w-2xl text-sm leading-6 text-gray-600"
                  >
                    Quando attiva, tutte le cliniche collegate usano soltanto il
                    listino della società per le nuove prescrizioni. I listini
                    delle cliniche e dei medici sono sospesi e restano salvati:
                    disattivando la condivisione tornano disponibili. Le
                    prescrizioni già create mantengono i propri prezzi.
                    L’impostazione si applica dopo il salvataggio, anche alle
                    cliniche collegate in seguito.
                  </p>
                  <p
                    id="organization-share-availability"
                    className="mt-2 text-sm leading-6 text-gray-600"
                  >
                    {!isEdit
                      ? 'Crea prima la società, poi aggiungi prestazioni o usa il listino di una delle sue cliniche. La condivisione parte disattivata.'
                      : !canManageOrganizationPriceList
                        ? 'Per preparare il listino e attivare la condivisione sono necessari i permessi di gestione società e listini del laboratorio.'
                        : priceListOverview === undefined
                          ? 'Verifica delle prestazioni del listino in corso…'
                          : priceListOverview.organizationServiceCount === 0
                            ? 'Il listino della società è vuoto. Aggiungi una prestazione o usa il listino di una clinica prima di attivarlo.'
                            : `Il listino della società contiene ${priceListOverview.organizationServiceCount} ${priceListOverview.organizationServiceCount === 1 ? 'prestazione attiva' : 'prestazioni attive'}.`}
                  </p>
                </div>
              </div>
            </fieldset>

            {canManage && (
              <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-end">
                <Button asChild variant="outline">
                  <Link to="/organizations">Torna all’elenco</Link>
                </Button>
                <Button type="submit" disabled={busy}>
                  {saving ? (
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  ) : (
                    <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  {saving
                    ? 'Salvataggio…'
                    : isEdit
                      ? 'Salva società'
                      : 'Crea società'}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {organization && (
        <section
          aria-labelledby="organization-clinics-title"
          className="space-y-4"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2
                id="organization-clinics-title"
                className="text-lg font-semibold text-gray-950"
              >
                Cliniche collegate{' '}
                <span className="ml-1 font-normal text-gray-600 tabular-nums">
                  ({organization.clinic_count})
                </span>
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Ogni clinica conserva i propri dati, utenti e prescrizioni.
              </p>
            </div>
            {canManage && (
              <Button asChild variant="outline">
                <Link to={`/companies/new?organizationId=${organization._id}`}>
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Nuova clinica
                </Link>
              </Button>
            )}
          </div>
          <Card className="overflow-hidden shadow-none">
            {organization.clinics.length === 0 ? (
              <div className="p-6 text-sm leading-6 text-gray-600">
                <Building2
                  className="mb-2 h-6 w-6 text-gray-500"
                  aria-hidden="true"
                />
                Nessuna clinica collegata. Collega una clinica esistente o
                creane una nuova per questa società.
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {organization.clinics.map((clinic) => (
                  <li
                    key={clinic._id}
                    className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-gray-950">
                        {clinic.nome}
                      </p>
                      {clinic.citta && (
                        <p className="mt-1 text-sm text-gray-600">
                          {clinic.citta}
                        </p>
                      )}
                    </div>
                    {canManage && (
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="min-h-10"
                      >
                        <Link
                          to={`/companies/${clinic._id}/edit`}
                          aria-label={`Modifica clinica ${clinic.nome}`}
                        >
                          Modifica clinica
                        </Link>
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canManage && (
              <div className="space-y-3 border-t border-gray-200 bg-gray-50/70 p-5">
                <label
                  htmlFor="organization-clinic"
                  className="block text-sm font-medium text-gray-900"
                >
                  Collega una clinica esistente
                </label>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <select
                    id="organization-clinic"
                    value={selectedClinic}
                    disabled={
                      busy ||
                      companiesResult === undefined ||
                      organizations === undefined ||
                      availableClinics.length === 0
                    }
                    onChange={(event) => {
                      setSelectedClinic(event.target.value)
                      setLinkError('')
                      setLinkStatus('')
                    }}
                    className={selectClass}
                    aria-describedby="organization-clinic-hint"
                  >
                    <option value="">
                      {companiesResult === undefined
                        ? 'Caricamento cliniche…'
                        : availableClinics.length === 0
                          ? 'Nessuna clinica da collegare'
                          : 'Seleziona una clinica'}
                    </option>
                    {availableClinics.map((clinic) => (
                      <option key={clinic._id} value={clinic._id}>
                        {clinic.nome}
                        {clinic.organization_id
                          ? ` · ${organizations?.find((item) => item._id === clinic.organization_id)?.nome ?? 'Altra società'}`
                          : ' · Senza società'}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void linkClinic()}
                    disabled={busy || !selectedClinic}
                    className="shrink-0"
                  >
                    {assigning ? (
                      <Loader2
                        className="mr-2 h-4 w-4 animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    )}
                    {assigning ? 'Collegamento…' : 'Collega clinica'}
                  </Button>
                </div>
                <p
                  id="organization-clinic-hint"
                  className="text-sm leading-6 text-gray-600"
                >
                  {clinicToLink?.organization_id
                    ? `La clinica sarà trasferita da “${previousOrganization?.nome ?? 'un’altra società'}” a questa società. Se la condivisione è attiva, userà soltanto il listino di questa società. Il collegamento si salva subito.`
                    : organization.share_price_lists
                      ? 'La clinica userà soltanto il listino della società per le nuove prescrizioni. Il collegamento si salva subito e gli accessi degli utenti restano invariati.'
                      : 'Il collegamento si salva subito. Gli accessi degli utenti alla clinica restano invariati.'}
                </p>
                {linkError && (
                  <p role="alert" className="text-sm text-red-800">
                    {linkError}
                  </p>
                )}
                {linkStatus && (
                  <p role="status" className="text-sm text-green-800">
                    {linkStatus}
                  </p>
                )}
              </div>
            )}
          </Card>
          {isLabSide && hasPermission('view_listino') && (
            <div className="flex flex-col gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-950">
                  Listino della società
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Consulta le prestazioni e prepara il listino da condividere.
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button asChild variant="outline">
                  <Link to={`/listino?organizationId=${organization._id}`}>
                    Apri listino società
                  </Link>
                </Button>
                {hasPermission('manage_listino') && (
                  <Button asChild variant="outline">
                    <Link
                      to={`/listino/new?organizationId=${organization._id}`}
                    >
                      Aggiungi prestazione
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          )}
          {canManageOrganizationPriceList && (
            <OrganizationPriceListAdoption
              organizationId={organization._id}
              overview={priceListOverview}
              sharingEnabled={organization.share_price_lists}
              disabled={saving || assigning || deleting}
              onBusyChange={setAdoptingPriceList}
            />
          )}
          {canManage && (
            <div className="flex flex-col gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-600">
                Una società si può eliminare solo se non ha cliniche o listini
                collegati.
              </p>
              <Button
                type="button"
                variant="outline"
                disabled={busy || organization.clinic_count > 0}
                onClick={() => void removeOrganization()}
                className="shrink-0 text-red-700 hover:text-red-800"
              >
                <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                {deleting ? 'Eliminazione…' : 'Elimina società'}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
